import { addDays, addMonths, differenceInCalendarDays, format, parseISO } from "date-fns";

const iso = (d) => format(d, "yyyy-MM-dd");

export const SERVICES = [
  {
    key: "vat", label: "VAT returns", category: "VAT", title: "VAT return", duration: 180, lead: 14, priority: "high",
    frequency: "quarterly", freqOptions: ["quarterly", "monthly"], periodLabel: "When does the next VAT period end?",
    dueLabel: "Return & payment due", dueFrom: (pe) => addDays(addMonths(pe, 1), 7), help: "HMRC deadline is 1 month and 7 days after the period ends.",
  },
  {
    key: "accounts", label: "Year-end accounts", category: "Accounts", title: "Year-end accounts", duration: 600, lead: 60, priority: "medium",
    frequency: "annually", periodLabel: "Accounting year end date", dueLabel: "Accounts due date",
    dueFrom: (pe) => addMonths(pe, 9), help: "Companies House deadline is normally 9 months after year end.",
  },
  {
    key: "management_accounts", label: "Management accounts", category: "Management Accounts", title: "Management accounts", duration: 240, lead: 10, priority: "medium",
    frequency: "monthly", freqOptions: ["monthly", "quarterly"], periodLabel: "When does the next period end?", dueLabel: "Complete by", noStatutory: true,
    dueFrom: (pe) => addDays(pe, 30), help: "No statutory deadline. Set how many days after the period end in Settings → Deadlines (default 30 days).",
  },
  {
    key: "corporation_tax", label: "Corporation Tax (CT600)", category: "Corporation Tax", title: "Corporation Tax return (CT600)", duration: 240, lead: 30, priority: "high",
    frequency: "annually", periodLabel: "Accounting year end date", dueLabel: "CT600 filing due",
    dueFrom: (pe) => addMonths(pe, 12), help: "Filing is due 12 months after year end (payment 9 months and 1 day).",
  },
  {
    key: "self_assessment", label: "Self Assessment tax return", category: "Self Assessment", title: "Self Assessment return", duration: 180, lead: 30, priority: "medium",
    frequency: "annually", taxYear: true, dueLabel: "Online filing due",
    dueFrom: (pe) => new Date(pe.getFullYear() + 1, 0, 31), help: "Online returns are due 31 January after the tax year ends.",
  },
  {
    key: "payroll", label: "Payroll", category: "Payroll", title: "Payroll run", duration: 60, lead: 3, priority: "high",
    frequency: "monthly", freqOptions: ["weekly", "monthly"], periodLabel: "Next pay date", hideDue: true,
    dueFrom: (pe) => pe, help: "Each pay date becomes a job card.",
  },
  {
    key: "bookkeeping", label: "Bookkeeping", category: "Bookkeeping", title: "Bookkeeping reconciliation", duration: 120, lead: 7, priority: "medium",
    frequency: "monthly", periodLabel: "Next month end to reconcile", dueLabel: "Complete by",
    dueFrom: (pe) => addDays(pe, 14), help: "Reconcile within two weeks of each month end.",
  },
];

const thisYear = new Date().getFullYear();
export const TAX_YEARS = [thisYear - 3, thisYear - 2, thisYear - 1, thisYear].map((y) => `${y - 1}/${String(y).slice(2)}`);

export const taxYearEnd = (ty) => `20${ty.slice(-2)}-04-05`;

export const SERVICE_OPTIONS = [...SERVICES.map((s) => ({ value: s.key, label: s.label })), { value: "other", label: "Other" }];

export const CLIENT_TYPES = [
  { value: "limited_company", label: "Limited Company" },
  { value: "sole_trader", label: "Sole Trader" },
  { value: "partnership", label: "Partnership" },
  { value: "individual", label: "Individual" },
];

export const clientTypeLabel = (c) => {
  const base = CLIENT_TYPES.find((t) => t.value === c?.client_type)?.label;
  if (!base) return "";
  return c.client_type === "individual" && c.partner_director ? `${base} · Partner/Director` : base;
};

export function computeDue(service, periodEnd) {
  return periodEnd ? iso(service.dueFrom(parseISO(periodEnd))) : "";
}

export function computeDates(service, periodEnd, rule) {
  if (!periodEnd) return { due: "", statutory: "" };
  const statutory = computeDue(service, periodEnd);
  const internal = rule && (rule.months || rule.days) ? iso(addDays(addMonths(parseISO(periodEnd), rule.months || 0), rule.days || 0)) : statutory;
  return { due: internal, statutory };
}

const after = (pe, r) => iso(addDays(addMonths(parseISO(pe), Number(r.months) || 0), Number(r.days) || 0));
export const hasTemplateDates = (tpl) => Boolean(tpl && (tpl.start_rule || tpl.internal_rule || tpl.deadline_rule));

export function datesFor(service, periodEnd, rule, tpl) {
  const base = computeDates(service, periodEnd, rule);
  if (!periodEnd || !hasTemplateDates(tpl)) return base;
  const due = tpl.internal_rule ? after(periodEnd, tpl.internal_rule) : base.due;
  const out = { due, statutory: tpl.deadline_rule ? after(periodEnd, tpl.deadline_rule) : base.statutory };
  if (tpl.start_rule) out.lead = String(Math.max(0, differenceInCalendarDays(parseISO(due), parseISO(after(periodEnd, tpl.start_rule)))));
  return out;
}
